import React, { useMemo, useState } from "react"
import { Pressable, ScrollView, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import moment from "moment"

import FilledButtonM from "@common/components/button/FilledButtonM"
import { IconImageM } from "@common/components/icons/IconsM"
import MobileDatePickerModal from "@common/components/modal/MobileDatePickerModal"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { filterSchoolSubjectsByClass } from "@data/utility/Utils"

import { SectionHeader } from "./SectionHeader"
import {
    DEADLINE_OPTIONS,
    HOUR_OPTIONS,
    formatOptions,
    sectionStyle,
    stickyFooterStyle,
} from "./utils"

const renderRadioPill = (selected, label, onPress, key) => (
    <Pressable
        key={key}
        onPress={onPress}
        style={{
            paddingVertical: 10,
            paddingHorizontal: 18,
            borderRadius: 22,
            borderWidth: 1.5,
            borderColor: selected ? theme.colors.primary : theme.colors.light,
            backgroundColor: selected
                ? theme.colors.primary
                : theme.colors.white,
            marginRight: 10,
            marginBottom: 10,
        }}
    >
        <TextM
            color={selected ? theme.colors.white : theme.colors.dark}
            fontWeight={selected ? "Bold" : "Medium"}
            fontSize={14}
        >
            {label}
        </TextM>
    </Pressable>
)

/**
 * Step 1 — parameters: hours, deadline, format, subject.
 *
 * Reads/writes the shared `formValues` object. Renders its own
 * "Continuer" button calling `goToNextStep` once the step is valid.
 * Validation is local (no react-hook-form fields registered) so the
 * built-in next button is disabled via `showNextButton: false` and
 * we drive navigation manually.
 */
export const ParametersStep = ({
    formValues,
    setFormValues,
    goToNextStep,
    schoolSubjects,
    user,
    extraHourPricePerHour,
}) => {
    const [datePickerVisible, setDatePickerVisible] = useState(false)

    const update = (patch) => setFormValues({ ...(formValues ?? {}), ...patch })

    const hours = formValues?.hours ?? 1
    const deadlineType = formValues?.deadline_type ?? "7_days"
    const customDeadline = formValues?.custom_deadline ?? null
    const format = formValues?.format ?? "shared"
    const subjectId = formValues?.subject_id ?? null

    const studentClass = user?.profile?.class
    const filteredSubjects = useMemo(
        () => filterSchoolSubjectsByClass(schoolSubjects, studentClass),
        [schoolSubjects, studentClass]
    )

    const objectiveSubjectIds = useMemo(() => {
        const ids = new Set()
        ;(user?.pedagogical_objectives ?? []).forEach((obj) => {
            ;(obj?.subject_ids ?? []).forEach((id) => ids.add(Number(id)))
        })
        return ids
    }, [user?.pedagogical_objectives])

    const objectiveSubjects = useMemo(
        () => filteredSubjects.filter((s) => objectiveSubjectIds.has(s.id)),
        [filteredSubjects, objectiveSubjectIds]
    )

    // "Voir d'autres matières" expander — local state because it
    // doesn't ride along in the submit payload. Seeded from the
    // currently-selected subject so a parent who picked an off-objective
    // matière, went forward, then came back, still sees their pick
    // (the expander would otherwise collapse on remount and hide it).
    const [showAllSubjects, setShowAllSubjects] = useState(
        () =>
            subjectId !== null &&
            objectiveSubjectIds.size > 0 &&
            !objectiveSubjectIds.has(subjectId)
    )
    // "Voir d'autres matières" surfaces subjects not yet in the parent's
    // objectives — still gated by the student's class via
    // `min_student_class` (the same filter applied to objectiveSubjects)
    // so the picker never offers a matière the student isn't old enough
    // for.
    const otherSubjects = useMemo(
        () => filteredSubjects.filter((s) => !objectiveSubjectIds.has(s.id)),
        [filteredSubjects, objectiveSubjectIds]
    )

    const visibleSubjects =
        showAllSubjects || objectiveSubjects.length === 0
            ? [...objectiveSubjects, ...otherSubjects]
            : objectiveSubjects

    const isIndividual = format === "individual"

    // Deadline only applies to shared: individual format hides the
    // section because the parent picks exact (date+time) values in
    // step 2, so deadline_type is irrelevant for validation here.
    // Custom dates must be ≥ 3 days out — earlier than that, the
    // parent should use the "Sous 3 jours" preset instead.
    const customDeadlineValid =
        isIndividual ||
        deadlineType !== "custom" ||
        (customDeadline &&
            moment(customDeadline).isSameOrAfter(
                moment().startOf("day").add(3, "days")
            ))

    const stepValid = !!hours && !!format && !!subjectId && customDeadlineValid

    const renderFormatCard = (option) => {
        const selected = format === option.value
        return (
            <Pressable
                key={option.value}
                onPress={() => update({ format: option.value })}
                style={{
                    flex: 1,
                    padding: 14,
                    borderRadius: 14,
                    borderWidth: 2,
                    borderColor: selected
                        ? theme.colors.primary
                        : theme.colors.lightMore,
                    backgroundColor: selected
                        ? theme.colors.material.primary["50"]
                        : theme.colors.white,
                    marginRight: option.value === "shared" ? 10 : 0,
                }}
            >
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        marginBottom: 8,
                    }}
                >
                    <View
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 14,
                            backgroundColor: selected
                                ? theme.colors.primary
                                : theme.colors.greyBg,
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: 8,
                        }}
                    >
                        <FontAwesome5
                            name={option.icon}
                            size={13}
                            color={
                                selected
                                    ? theme.colors.white
                                    : theme.colors.primary
                            }
                            solid
                        />
                    </View>
                    <TextM
                        fontWeight="Bold"
                        fontSize={14}
                        wrap
                        style={{ flex: 1, flexShrink: 1 }}
                    >
                        {option.title}
                    </TextM>
                </View>
                <TextM
                    fontWeight="ExtraBold"
                    fontSize={15}
                    color={theme.colors.primary}
                    style={{ marginBottom: 4 }}
                >
                    {option.price}
                </TextM>
                <TextM
                    fontSize={11}
                    color={theme.colors.greyAlt}
                    wrap
                    style={{ lineHeight: 15 }}
                >
                    {option.subtitle}
                </TextM>
            </Pressable>
        )
    }

    const renderSubjectChip = (subject) => {
        const selected = subjectId === subject.id
        const tint = subject.color
            ? subject.color + "22"
            : theme.colors.material.primary["50"]
        return (
            <Pressable
                key={subject.id}
                onPress={() => update({ subject_id: subject.id })}
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 999,
                    borderWidth: 1.5,
                    borderColor: selected
                        ? subject.color || theme.colors.primary
                        : theme.colors.lightMore,
                    backgroundColor: selected ? tint : theme.colors.white,
                    marginRight: 8,
                    marginBottom: 8,
                }}
            >
                {subject.icon && <IconImageM uri={subject.icon} width={18} />}
                <TextM
                    style={{ marginLeft: subject.icon ? 6 : 0 }}
                    fontWeight={selected ? "Bold" : "Medium"}
                    fontSize={13}
                    color={
                        selected
                            ? subject.color || theme.colors.primary
                            : theme.colors.dark
                    }
                >
                    {subject.name}
                </TextM>
            </Pressable>
        )
    }

    return (
        <View style={{ flex: 1, width: "100%" }}>
            <ScrollView
                style={{ width: "100%", flex: 1 }}
                contentContainerStyle={{
                    paddingHorizontal: 20,
                    paddingBottom: 12,
                }}
                keyboardShouldPersistTaps="handled"
            >
                <View style={sectionStyle}>
                    <SectionHeader icon="clock" label="Combien d'heures ?" />
                    <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
                        {HOUR_OPTIONS.map((h) =>
                            renderRadioPill(
                                hours === h,
                                `${h} heure${h > 1 ? "s" : ""}`,
                                // requested_dates is realigned by the
                                // IndividualDatesStep effect on mount,
                                // selected_slots has no cap on shared —
                                // nothing else to trim here.
                                () => update({ hours: h }),
                                h
                            )
                        )}
                    </View>
                </View>

                <View style={sectionStyle}>
                    <SectionHeader icon="th-large" label="Quel format ?" />
                    <View style={{ flexDirection: "row" }}>
                        {formatOptions(extraHourPricePerHour).map(
                            renderFormatCard
                        )}
                    </View>
                </View>

                {format !== "individual" && (
                    <View style={sectionStyle}>
                        <SectionHeader
                            icon="calendar-alt"
                            label="Pour quand ?"
                        />
                        <View
                            style={{ flexDirection: "row", flexWrap: "wrap" }}
                        >
                            {DEADLINE_OPTIONS.map((opt) =>
                                renderRadioPill(
                                    deadlineType === opt.value,
                                    opt.label,
                                    () => update({ deadline_type: opt.value }),
                                    opt.value
                                )
                            )}
                        </View>
                        {deadlineType === "custom" && (
                            <Pressable
                                onPress={() => setDatePickerVisible(true)}
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    padding: 12,
                                    borderWidth: 1,
                                    borderColor: theme.colors.light,
                                    borderRadius: 8,
                                    marginTop: 4,
                                }}
                            >
                                <FontAwesome5
                                    name="calendar"
                                    size={14}
                                    color={theme.colors.primary}
                                    style={{ marginRight: 8 }}
                                    solid
                                />
                                <TextM>
                                    {customDeadline
                                        ? moment(customDeadline).format(
                                              "DD/MM/YYYY"
                                          )
                                        : "Choisir une date"}
                                </TextM>
                            </Pressable>
                        )}
                    </View>
                )}

                <View style={sectionStyle}>
                    <SectionHeader icon="book-open" label="Quelle matière ?" />
                    <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
                        {visibleSubjects.map(renderSubjectChip)}
                    </View>
                    {otherSubjects.length > 0 &&
                        objectiveSubjects.length > 0 && (
                            <Pressable
                                onPress={() =>
                                    setShowAllSubjects((prev) => !prev)
                                }
                                style={{
                                    alignSelf: "flex-start",
                                    paddingVertical: 6,
                                    paddingHorizontal: 10,
                                    marginTop: 2,
                                }}
                            >
                                <TextM
                                    fontWeight="Bold"
                                    fontSize={13}
                                    color={theme.colors.primary}
                                >
                                    {showAllSubjects
                                        ? "− Masquer les autres matières"
                                        : "+ Voir d'autres matières"}
                                </TextM>
                            </Pressable>
                        )}
                </View>
            </ScrollView>

            <View style={stickyFooterStyle}>
                <FilledButtonM
                    label="Continuer"
                    onPress={() => goToNextStep()}
                    disabled={!stepValid}
                    style={{ width: "100%" }}
                />
            </View>

            <MobileDatePickerModal
                visible={datePickerVisible}
                onDismiss={() => setDatePickerVisible(false)}
                value={customDeadline}
                validRange={{
                    // "Sous 3 jours" is the minimum deadline preset — a
                    // custom date earlier than 3 days from now would
                    // shortcut a faster turnaround than the platform
                    // offers, so the picker starts at now + 3 days.
                    startDate: moment().add(3, "days").toDate(),
                    endDate: moment().add(30, "days").toDate(),
                }}
                onConfirm={(dd) => {
                    setDatePickerVisible(false)
                    update({ custom_deadline: dd })
                }}
                label="Échéance souhaitée"
            />
        </View>
    )
}
